import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Camera, ArrowRight, ArrowLeft, CheckCircle2, Upload, X, Plus,
  User, Briefcase, FileText, Image, Clock, Loader2, Globe, Instagram, Facebook, MapPin, Trash2
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { loadGoogleMapsScript } from '@/utils/googleMapsLoader';
import { initPlaceAutocomplete, destroyPlaceAutocomplete } from '@/utils/placeAutocomplete';
import "@/styles/commandDeck.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// --- Command Deck shared style tokens --------------------------------------
const CD_INPUT = {
  width: '100%',
  padding: '12px 14px',
  border: '1px solid rgba(255,255,255,.12)',
  borderRadius: '11px',
  background: 'rgba(255,255,255,.04)',
  color: '#fff',
  fontSize: '13.5px',
  outline: 'none',
};
const CD_LABEL = {
  display: 'block',
  fontSize: '11.5px',
  color: 'rgba(255,255,255,.5)',
  marginBottom: '6px',
};

const CrewOnboarding = ({ user: userProp, onUpdateUser }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialStep = parseInt(searchParams.get('step')) || 1;
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const portfolioInputRef = useRef(null);

  // Fallback to localStorage if user prop is null (race condition after signup)
  const user = userProp || (() => {
    try {
      const stored = localStorage.getItem('shootzu_user');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  })();

  // Form data across all steps
  const [formData, setFormData] = useState({
    // Step 2: Profile Details
    profile_photo_url: user?.profile_photo_url || "",
    profile_photo_key: user?.profile_photo_key || "",
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    location: user?.location || "",
    location_lat: user?.location_lat || null,
    location_lng: user?.location_lng || null,
    website_url: user?.website_url || "",
    facebook_url: user?.facebook_url || "",
    instagram_url: user?.instagram_url || "",

    // Step 3: Roles & ABN
    skills: user?.skills || [],
    equipment_list: user?.equipment_list || [],
    // Only populate ABN if already verified - otherwise start fresh
    abn: (user?.abn_verified && user?.abn) ? user.abn : "",
    business_name: (user?.abn_verified && user?.business_name) ? user.business_name : "",
    gst_registered: (user?.abn_verified && user?.gst_registered) ? user.gst_registered : false,
    abn_verified: user?.abn_verified || false,

    // Step 4: About You
    bio: user?.bio || "",
    experience_years: user?.experience_years || "",

    // Step 5: Showcase
    portfolio_images: user?.portfolio_images || [],
    video_links: user?.video_links || []
  });

  // ABN validation state - only set as validated if there's actually a verified ABN
  const [abnValidated, setAbnValidated] = useState(
    Boolean(user?.abn_verified && user?.abn)
  );
  const [validatingABN, setValidatingABN] = useState(false);
  const [abnResult, setAbnResult] = useState(null);
  const [businessNames, setBusinessNames] = useState([]);
  const [abnError, setAbnError] = useState("");

  // Photo upload state
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingPortfolio, setUploadingPortfolio] = useState(false);

  // Video link input
  const [newVideoLink, setNewVideoLink] = useState("");

  // Update form data when user prop changes (e.g., after loading from localStorage)
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        profile_photo_url: user.profile_photo_url || prev.profile_photo_url,
        profile_photo_key: user.profile_photo_key || prev.profile_photo_key,
        first_name: user.first_name || prev.first_name,
        last_name: user.last_name || prev.last_name,
        email: user.email || prev.email,
        phone: user.phone || prev.phone,
        location: user.location || prev.location,
        location_lat: user.location_lat || prev.location_lat,
        location_lng: user.location_lng || prev.location_lng,
        website_url: user.website_url || prev.website_url,
        facebook_url: user.facebook_url || prev.facebook_url,
        instagram_url: user.instagram_url || prev.instagram_url,
        skills: user.skills || prev.skills,
        equipment_list: user.equipment_list || prev.equipment_list,
        // Only populate ABN fields if already verified
        abn: (user.abn_verified && user.abn) ? user.abn : prev.abn,
        business_name: (user.abn_verified && user.business_name) ? user.business_name : prev.business_name,
        gst_registered: (user.abn_verified && user.gst_registered) ? user.gst_registered : prev.gst_registered,
        abn_verified: user.abn_verified || prev.abn_verified,
        bio: user.bio || prev.bio,
        experience_years: user.experience_years || prev.experience_years,
        portfolio_images: user.portfolio_images || prev.portfolio_images,
        video_links: user.video_links || prev.video_links
      }));
    }
  }, [user]);

  // Initialize Google Maps autocomplete for location field when on step 2
  useEffect(() => {
    if (currentStep === 2) {
      loadGoogleMapsScript(() => {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          initPlaceAutocomplete(
            'location-autocomplete-container',
            (place) => {
              setFormData(prev => ({
                ...prev,
                location: place.formatted_address,
                location_lat: place.lat,
                location_lng: place.lng
              }));
            },
            {
              types: ['(cities)'],
              placeholder: 'Start typing a suburb…',
              className: 'w-full px-3.5 py-3 rounded-[11px] bg-white/[0.04] border border-white/[0.12] text-white text-sm placeholder:text-white/40 focus:border-[#28C0D0] outline-none transition-all'
            },
            formData.location
          );
        }, 100);
      });
    }

    return () => {
      if (currentStep === 2) {
        destroyPlaceAutocomplete('location-autocomplete-container');
      }
    };
  }, [currentStep]);

  const steps = [
    { number: 1, title: "Welcome", icon: Clock },
    { number: 2, title: "Details", icon: User },
    { number: 3, title: "Roles", icon: Briefcase },
    { number: 4, title: "About You", icon: FileText },
    { number: 5, title: "Showcase", icon: Image }
  ];

  const skillOptions = [
    { value: "photographer", label: "Photographer" },
    { value: "videographer", label: "Videographer" },
    { value: "editor_photos", label: "Editor (Photos)" },
    { value: "editor_video", label: "Editor (Video)" },
    { value: "assistant", label: "Assistant" },
    { value: "drone_operator", label: "Drone Operator" },
    { value: "voxpop_videographer", label: "VoxPop Videographer" }
  ];

  const equipmentOptions = [
    "DSLR Camera",
    "Mirrorless Camera",
    "Cinema Camera",
    "Drone (Licensed)",
    "Lighting Kit",
    "Audio Kit",
    "Gimbal/Stabilizer",
    "Green Screen",
    "Editing Suite",
    "Studio Space"
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSkillToggle = (skill) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }));
  };

  const handleEquipmentToggle = (equipment) => {
    setFormData(prev => ({
      ...prev,
      equipment_list: prev.equipment_list.includes(equipment)
        ? prev.equipment_list.filter(e => e !== equipment)
        : [...prev.equipment_list, equipment]
    }));
  };

  // Profile photo upload
  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (!user?.id) {
      toast.error("Please log in again to upload photos");
      return;
    }

    try {
      setUploadingPhoto(true);

      // Step 1: Get presigned URL
      const uploadUrlResponse = await axios.post(
        `${API}/profile/photo/upload-url`,
        null,
        {
          params: {
            user_id: user.id,
            filename: file.name,
            content_type: file.type
          }
        }
      );

      const { url, fields, object_key } = uploadUrlResponse.data;

      // Step 2: Upload to S3 using form data
      const uploadFormData = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        uploadFormData.append(key, value);
      });
      uploadFormData.append('file', file);

      await axios.post(url, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Step 3: Confirm upload
      await axios.post(
        `${API}/profile/photo/confirm`,
        null,
        {
          params: {
            user_id: user.id,
            object_key: object_key
          }
        }
      );

      // Generate a temporary URL for display
      const displayUrl = URL.createObjectURL(file);
      handleChange("profile_photo_url", displayUrl);
      handleChange("profile_photo_key", object_key);
      toast.success("Profile photo uploaded successfully");
    } catch (error) {
      console.error("Photo upload error:", error);
      toast.error("Failed to upload photo. Please try again.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Portfolio image upload
  const handlePortfolioUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const imageFiles = files.filter(f => f.type.startsWith("image/"));
    if (imageFiles.length === 0) {
      toast.error("Please select image files only");
      return;
    }

    try {
      setUploadingPortfolio(true);
      const uploadedImages = [...formData.portfolio_images];

      for (const file of imageFiles) {
        // Step 1: Get presigned URL for portfolio
        const uploadUrlResponse = await axios.post(
          `${API}/profile/portfolio/upload-url`,
          null,
          {
            params: {
              user_id: user.id,
              filename: file.name,
              content_type: file.type
            }
          }
        );

        const { url, fields, object_key } = uploadUrlResponse.data;

        // Step 2: Upload to S3 using form data
        const uploadFormData = new FormData();
        Object.entries(fields).forEach(([key, value]) => {
          uploadFormData.append(key, value);
        });
        uploadFormData.append('file', file);

        await axios.post(url, uploadFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        // Use a temporary URL for display
        const displayUrl = URL.createObjectURL(file);
        uploadedImages.push({
          key: object_key,
          url: displayUrl
        });
      }

      handleChange("portfolio_images", uploadedImages);
      toast.success(`${imageFiles.length} image(s) uploaded successfully`);
    } catch (error) {
      console.error("Portfolio upload error:", error);
      toast.error("Failed to upload images. Please try again.");
    } finally {
      setUploadingPortfolio(false);
    }
  };

  const removePortfolioImage = (index) => {
    const updated = formData.portfolio_images.filter((_, i) => i !== index);
    handleChange("portfolio_images", updated);
  };

  // Video links
  const addVideoLink = () => {
    if (!newVideoLink.trim()) return;

    // Basic URL validation
    if (!newVideoLink.startsWith("http")) {
      toast.error("Please enter a valid URL starting with http:// or https://");
      return;
    }

    handleChange("video_links", [...formData.video_links, newVideoLink.trim()]);
    setNewVideoLink("");
  };

  const removeVideoLink = (index) => {
    const updated = formData.video_links.filter((_, i) => i !== index);
    handleChange("video_links", updated);
  };

  // ABN validation
  const formatABN = (value) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
    if (digits.length <= 8) return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`;
    return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 11)}`;
  };

  const handleABNChange = (value) => {
    const formatted = formatABN(value);
    handleChange("abn", formatted);

    if (abnValidated) {
      setAbnValidated(false);
      setAbnResult(null);
      setBusinessNames([]);
      handleChange("business_name", "");
      setAbnError("");
    }
  };

  const handleValidateABN = async () => {
    const abnDigits = formData.abn.replace(/\s/g, "");

    if (abnDigits.length !== 11) {
      setAbnError("ABN must be 11 digits");
      return;
    }

    try {
      setValidatingABN(true);
      setAbnError("");

      const response = await axios.post(`${API}/abn/validate`, {
        abn: abnDigits
      }, { timeout: 30000 });

      if (response.data.is_valid) {
        setAbnResult(response.data);
        const names = response.data.business_names || [];
        setBusinessNames(names);
        setAbnValidated(true);

        if (names.length === 1) {
          handleChange("business_name", names[0]);
        }

        handleChange("gst_registered", response.data.gst_registered);
        handleChange("abn_verified", true);

        toast.success("ABN verified with Australian Business Register");
      } else {
        setAbnError(response.data.error || "ABN validation failed");
        toast.error(response.data.error || "ABN validation failed");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || "Invalid ABN or service unavailable";
      setAbnError(errorMessage);
      toast.error(errorMessage);
      setAbnValidated(false);
    } finally {
      setValidatingABN(false);
    }
  };

  // Save progress for current step
  const saveProgress = async () => {
    try {
      setLoading(true);

      await axios.put(`${API}/users/${user.id}`, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        website_url: formData.website_url,
        facebook_url: formData.facebook_url,
        instagram_url: formData.instagram_url,
        skills: formData.skills,
        equipment_list: formData.equipment_list,
        abn: formData.abn.replace(/\s/g, ""),
        business_name: formData.business_name,
        gst_registered: formData.gst_registered,
        abn_verified: formData.abn_verified,
        bio: formData.bio,
        experience_years: formData.experience_years ? parseInt(formData.experience_years) : null,
        // Extract just the S3 keys from portfolio images
        portfolio_images: formData.portfolio_images.map(img => typeof img === 'string' ? img : img.key),
        video_links: formData.video_links
      });

      return true;
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save progress");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Navigation
  const goToNextStep = async () => {
    // Validate current step before proceeding
    if (currentStep === 2) {
      if (!formData.first_name || !formData.last_name) {
        toast.error("First name and last name are required");
        return;
      }
      if (!formData.phone) {
        toast.error("Phone number is required");
        return;
      }
    }

    if (currentStep === 3) {
      if (formData.skills.length === 0) {
        toast.error("Please select at least one role");
        return;
      }
      if (!formData.abn_verified) {
        toast.error("Please enter and verify your ABN");
        return;
      }
    }

    // Save progress
    if (currentStep > 1) {
      const saved = await saveProgress();
      if (!saved) return;
    }

    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goToPrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Submit for review
  const handleSubmitForReview = async () => {
    // Final validation
    if (!formData.first_name || !formData.last_name) {
      toast.error("Please complete your profile details");
      setCurrentStep(2);
      return;
    }
    if (formData.skills.length === 0) {
      toast.error("Please select at least one role");
      setCurrentStep(3);
      return;
    }

    try {
      setSubmitting(true);

      // Save all data
      await axios.put(`${API}/users/${user.id}`, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        full_name: `${formData.first_name} ${formData.last_name}`.trim(),
        phone: formData.phone,
        location: formData.location,
        location_lat: formData.location_lat,
        location_lng: formData.location_lng,
        website_url: formData.website_url,
        facebook_url: formData.facebook_url,
        instagram_url: formData.instagram_url,
        skills: formData.skills,
        equipment_list: formData.equipment_list,
        abn: formData.abn.replace(/\s/g, ""),
        business_name: formData.business_name,
        gst_registered: formData.gst_registered,
        abn_verified: formData.abn_verified,
        abn_validated_new_system: formData.abn_verified,
        bio: formData.bio,
        experience_years: formData.experience_years ? parseInt(formData.experience_years) : null,
        // Extract just the S3 keys from portfolio images (backend expects array of keys, not objects)
        portfolio_images: formData.portfolio_images.map(img => typeof img === 'string' ? img : img.key),
        video_links: formData.video_links,
        profile_complete: true,
        profile_submitted: true,
        status: "PENDING_APPROVAL",
        approval_status: "pending_approval"
      });

      // Update local user state
      if (onUpdateUser) {
        onUpdateUser({
          ...user,
          ...formData,
          equipment_list: formData.equipment_list,
          status: "PENDING_APPROVAL",
          profile_complete: true,
          abn_validated_new_system: formData.abn_verified
        });
      }

      toast.success("Profile submitted for review!");
      navigate("/pending-approval");
    } catch (error) {
      console.error("Submit error:", error);
      toast.error("Failed to submit profile. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Delete account handler
  const handleDeleteAccount = async () => {
    try {
      const userDataString = localStorage.getItem('shootzu_user');
      if (!userDataString) {
        toast.error('User not found');
        return;
      }

      const userData = JSON.parse(userDataString);
      const userId = userData.id;

      // Call backend to delete account
      await axios.delete(`${API}/users/account/${userId}`);

      // Clear all data and logout
      localStorage.clear();
      toast.success("Account deleted successfully. You can sign up again anytime.");

      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Failed to delete account. Please try again or contact support.');
    }
  };

  // Redirect if user is already approved
  useEffect(() => {
    if (user?.status === "APPROVED" || user?.is_approved) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  // Step 1: Welcome
  const renderWelcomeStep = () => {
    const items = [
      { icon: Camera, tint: "rgba(40,192,208,.16)", color: "#28C0D0", title: "A profile photo", sub: "So clients know who they're hiring." },
      { icon: User, tint: "rgba(103,198,180,.16)", color: "#67C6B4", title: "Contact details", sub: "Mobile and the towns you cover." },
      { icon: Briefcase, tint: "rgba(120,40,124,.2)", color: "#C98AD4", title: "A valid ABN", sub: "We vet every member — no exceptions." },
      { icon: Image, tint: "rgba(40,192,208,.16)", color: "#28C0D0", title: "A few portfolio shots", sub: "Show off your best work." },
    ];
    return (
      <div>
        <h2 className="cd-display" style={{ fontWeight: 800, fontSize: "24px", color: "#fff", letterSpacing: "-.5px", margin: 0 }}>
          Welcome to the Zu Crew{formData.first_name ? `, ${formData.first_name}` : ""}
        </h2>
        <p style={{ fontSize: "14px", color: "rgba(255,255,255,.6)", marginTop: "7px", lineHeight: 1.6 }}>
          Let's get your profile vetted so producers can hire you. It takes about{" "}
          <span style={{ color: "#28C0D0", fontWeight: 600 }}>5 minutes</span> — here's what you'll need.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: "11px", marginTop: "20px" }}>
          {items.map((it, i) => {
            const Ico = it.icon;
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "14px 16px", borderRadius: "13px", background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.08)" }}>
                <span style={{ width: "34px", height: "34px", borderRadius: "10px", background: it.tint, color: it.color, display: "flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto" }}>
                  <Ico className="w-[17px] h-[17px]" />
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#fff" }}>{it.title}</div>
                  <div style={{ fontSize: "12px", color: "rgba(255,255,255,.5)" }}>{it.sub}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Step 2: Profile Details
  const renderDetailsStep = () => (
    <div>
      <h2 className="cd-display" style={{ fontWeight: 800, fontSize: "22px", color: "#fff", letterSpacing: "-.5px", margin: 0 }}>Your details</h2>

      {/* Profile Photo */}
      <div style={{ display: "flex", alignItems: "center", gap: "16px", margin: "18px 0" }}>
        <div className="relative" style={{ flex: "0 0 auto" }}>
          {formData.profile_photo_url ? (
            <img
              src={formData.profile_photo_url}
              alt="Profile"
              style={{ width: "64px", height: "64px", borderRadius: "16px", objectFit: "cover" }}
            />
          ) : (
            <div style={{ width: "64px", height: "64px", borderRadius: "16px", background: "linear-gradient(135deg,#572B7C,#28C0D0)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}>
              <Camera className="w-6 h-6" />
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadingPhoto}
          style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "10px 16px", borderRadius: "11px", background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.16)", color: "#fff", fontWeight: 600, fontSize: "12.5px", cursor: "pointer" }}
        >
          {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          Upload photo
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleProfilePhotoUpload}
          className="hidden"
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
        {/* Name Fields */}
        <div>
          <Label style={CD_LABEL}>First name *</Label>
          <Input
            value={formData.first_name}
            onChange={(e) => handleChange("first_name", e.target.value)}
            placeholder="Jordan"
            style={CD_INPUT}
            className="placeholder:text-white/40 focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>
        <div>
          <Label style={CD_LABEL}>Last name *</Label>
          <Input
            value={formData.last_name}
            onChange={(e) => handleChange("last_name", e.target.value)}
            placeholder="Pratt"
            style={CD_INPUT}
            className="placeholder:text-white/40 focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>

        {/* Email (read-only) */}
        <div>
          <Label style={CD_LABEL}>Email</Label>
          <Input
            value={formData.email}
            disabled
            style={{ ...CD_INPUT, opacity: 0.6 }}
            className="focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>

        {/* Phone */}
        <div>
          <Label style={CD_LABEL}>Mobile *</Label>
          <Input
            value={formData.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
            placeholder="0412 345 678"
            style={CD_INPUT}
            className="placeholder:text-white/40 focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>

        {/* Location / City */}
        <div style={{ gridColumn: "span 2" }}>
          <Label style={CD_LABEL}>Town / city *</Label>
          <div id="location-autocomplete-container">
            {/* Google Places Autocomplete will be injected here */}
          </div>
          {formData.location && formData.location_lat && (
            <p style={{ color: "#67C6B4", fontSize: "11px", marginTop: "6px", display: "flex", alignItems: "center", gap: "4px" }}>
              <CheckCircle2 className="w-3 h-3" />
              Location verified
            </p>
          )}
        </div>
      </div>

      {/* Social Links */}
      <div style={{ marginTop: "16px" }}>
        <Label style={CD_LABEL}>Social &amp; web links (optional)</Label>
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Globe className="w-5 h-5" style={{ color: "rgba(255,255,255,.5)", flexShrink: 0 }} />
            <Input
              value={formData.website_url}
              onChange={(e) => handleChange("website_url", e.target.value)}
              placeholder="https://yourwebsite.com"
              style={CD_INPUT}
              className="placeholder:text-white/40 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Facebook className="w-5 h-5" style={{ color: "#28C0D0", flexShrink: 0 }} />
            <Input
              value={formData.facebook_url}
              onChange={(e) => handleChange("facebook_url", e.target.value)}
              placeholder="https://facebook.com/yourpage"
              style={CD_INPUT}
              className="placeholder:text-white/40 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Instagram className="w-5 h-5" style={{ color: "#C98AD4", flexShrink: 0 }} />
            <Input
              value={formData.instagram_url}
              onChange={(e) => handleChange("instagram_url", e.target.value)}
              placeholder="https://instagram.com/yourhandle"
              style={CD_INPUT}
              className="placeholder:text-white/40 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>
        </div>
      </div>
    </div>
  );

  // Step 3: Roles & ABN
  const renderRolesStep = () => (
    <div>
      <h2 className="cd-display" style={{ fontWeight: 800, fontSize: "22px", color: "#fff", letterSpacing: "-.5px", margin: 0 }}>Roles &amp; ABN</h2>
      <p style={{ fontSize: "12.5px", color: "rgba(255,255,255,.55)", marginTop: "6px" }}>Pick what you do — choose at least one.</p>

      {/* Skills Selection */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "14px" }}>
        {skillOptions.map((skill) => {
          const on = formData.skills.includes(skill.value);
          return (
            <button
              key={skill.value}
              type="button"
              onClick={() => handleSkillToggle(skill.value)}
              style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                fontSize: "12px", fontWeight: 600, padding: "8px 14px", borderRadius: "11px", cursor: "pointer",
                color: on ? "#0C1120" : "rgba(255,255,255,.7)",
                background: on ? "#28C0D0" : "rgba(255,255,255,.06)",
                border: on ? "1px solid #28C0D0" : "1px solid rgba(255,255,255,.1)",
              }}
            >
              {on && <CheckCircle2 className="w-3 h-3" />}
              {skill.label}
            </button>
          );
        })}
      </div>

      {/* Equipment Selection */}
      <div style={{ marginTop: "20px" }}>
        <Label style={CD_LABEL}>Your equipment (optional)</Label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "2px" }}>
          {equipmentOptions.map((equipment) => {
            const on = formData.equipment_list.includes(equipment);
            return (
              <button
                key={equipment}
                type="button"
                onClick={() => handleEquipmentToggle(equipment)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: "6px",
                  fontSize: "12px", fontWeight: 600, padding: "8px 14px", borderRadius: "11px", cursor: "pointer",
                  color: "#fff",
                  background: on ? "rgba(120,40,124,.3)" : "rgba(255,255,255,.06)",
                  border: on ? "1px solid #C98AD4" : "1px solid rgba(255,255,255,.1)",
                }}
              >
                {on && <CheckCircle2 className="w-3 h-3" style={{ color: "#C98AD4" }} />}
                {equipment}
              </button>
            );
          })}
        </div>
        {formData.equipment_list.length > 0 && (
          <p style={{ color: "#C98AD4", fontSize: "11.5px", marginTop: "8px" }}>
            {formData.equipment_list.length} item{formData.equipment_list.length > 1 ? 's' : ''} selected
          </p>
        )}
      </div>

      {/* ABN Section */}
      <div style={{ marginTop: "20px", paddingTop: "20px", borderTop: "1px solid rgba(255,255,255,.08)" }}>
        <Label style={CD_LABEL}>ABN (Australian Business Number) *</Label>
        <p style={{ fontSize: "12px", color: "rgba(255,255,255,.5)", margin: "0 0 8px" }}>You must hold a valid ABN to legally accept and post jobs via Shootzu</p>
        <div style={{ display: "flex", gap: "10px" }}>
          <Input
            value={formData.abn}
            onChange={(e) => handleABNChange(e.target.value)}
            placeholder="51 123 456 789"
            maxLength={14}
            disabled={abnValidated}
            className="cd-mono placeholder:text-white/40 focus-visible:ring-0 focus-visible:ring-offset-0"
            style={{ ...CD_INPUT, flex: 1, opacity: abnValidated ? 0.6 : 1 }}
          />
          {abnValidated ? (
            <Button
              type="button"
              onClick={() => {
                setAbnValidated(false);
                setAbnResult(null);
                setBusinessNames([]);
                handleChange("abn", "");
                handleChange("abn_verified", false);
                handleChange("business_name", "");
                handleChange("gst_registered", false);
              }}
              style={{ padding: "0 18px", borderRadius: "11px", background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.16)", color: "#fff", fontWeight: 600, fontSize: "13px", whiteSpace: "nowrap", height: "auto" }}
            >
              Change
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleValidateABN}
              disabled={validatingABN || formData.abn.replace(/\s/g, "").length !== 11}
              style={{ padding: "0 18px", borderRadius: "11px", background: "#28C0D0", border: "none", color: "#0C1120", fontWeight: 700, fontSize: "13px", whiteSpace: "nowrap", height: "auto" }}
            >
              {validatingABN ? <Loader2 className="w-4 h-4 animate-spin" /> : "Validate"}
            </Button>
          )}
        </div>
        <p style={{ fontSize: "11px", color: "rgba(255,255,255,.4)", marginTop: "6px" }}>Enter your 11-digit ABN to verify with the Australian Business Register</p>
        {abnError && <p style={{ color: "#F87171", fontSize: "12.5px", marginTop: "8px" }}>{abnError}</p>}

        {/* Validation Result */}
        {abnValidated && abnResult && (
          <div style={{ marginTop: "12px", padding: "14px 16px", borderRadius: "13px", background: "rgba(103,198,180,.08)", border: "1px solid rgba(103,198,180,.25)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: 600, color: "#67C6B4", marginBottom: "10px" }}>
              <CheckCircle2 className="w-4 h-4" />
              ABN Verified with ABR
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px 24px", fontSize: "12px", color: "rgba(255,255,255,.7)" }}>
              <div>
                <span style={{ color: "rgba(255,255,255,.45)" }}>Status: </span>
                <span style={{ color: "#fff", fontWeight: 600 }}>{abnResult.abn_status || 'Active'}</span>
              </div>
              <div>
                <span style={{ color: "rgba(255,255,255,.45)" }}>GST: </span>
                <span style={{ color: "#fff", fontWeight: 600 }}>{formData.gst_registered ? 'Registered' : 'Not registered'}</span>
              </div>
              {abnResult.entity_type && (
                <div>
                  <span style={{ color: "rgba(255,255,255,.45)" }}>Entity: </span>
                  <span style={{ color: "#fff", fontWeight: 600 }}>{abnResult.entity_type}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Business Name Selection */}
        {abnValidated && businessNames.length > 0 && (
          <div style={{ marginTop: "12px" }}>
            <Label style={CD_LABEL}>Select your business name *</Label>
            {businessNames.length === 1 ? (
              <div style={{ padding: "14px 16px", borderRadius: "13px", background: "rgba(40,192,208,.08)", border: "1px solid rgba(40,192,208,.25)", display: "flex", alignItems: "center", gap: "12px" }}>
                <Briefcase className="w-5 h-5" style={{ color: "#28C0D0", flexShrink: 0 }} />
                <span style={{ fontWeight: 600, color: "#fff" }}>{businessNames[0]}</span>
              </div>
            ) : (
              <select
                value={formData.business_name}
                onChange={(e) => handleChange("business_name", e.target.value)}
                style={CD_INPUT}
              >
                <option value="" style={{ color: "#000" }}>-- Select Business Name --</option>
                {businessNames.map((name, idx) => (
                  <option key={idx} value={name} style={{ color: "#000" }}>{name}</option>
                ))}
              </select>
            )}
            <p style={{ fontSize: "11px", color: "rgba(255,255,255,.4)", marginTop: "6px" }}>
              {businessNames.length === 1
                ? 'This is the registered business name for your ABN'
                : 'Multiple business names found - please select the one you use'}
            </p>
          </div>
        )}

        {/* Important Notice */}
        {abnValidated && (
          <div style={{ marginTop: "12px", padding: "14px 16px", borderRadius: "13px", background: "rgba(120,40,124,.12)", border: "1px solid rgba(201,138,212,.3)" }}>
            <p style={{ fontWeight: 600, color: "#C98AD4", fontSize: "12px", margin: "0 0 6px" }}>Important</p>
            <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "11.5px", color: "rgba(255,255,255,.65)", lineHeight: 1.6 }}>
              <li>Your business name is automatically set from ABR records</li>
              <li>This ensures compliance with Australian tax regulations</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );

  // Step 4: About You
  const renderAboutStep = () => (
    <div>
      <h2 className="cd-display" style={{ fontWeight: 800, fontSize: "22px", color: "#fff", letterSpacing: "-.5px", margin: 0 }}>About you</h2>

      {/* Bio */}
      <div style={{ marginTop: "16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
          <span style={{ fontSize: "11.5px", color: "rgba(255,255,255,.5)" }}>Bio</span>
          <span style={{ fontSize: "11px", color: "rgba(255,255,255,.4)" }}>{formData.bio.length} / 2000</span>
        </div>
        <Textarea
          value={formData.bio}
          onChange={(e) => handleChange("bio", e.target.value)}
          placeholder="Melbourne-based photographer & videographer with 8 years on weddings, brand films and live events. Calm on set, fast on turnaround…"
          maxLength={2000}
          rows={5}
          className="placeholder:text-white/40 focus-visible:ring-0 focus-visible:ring-offset-0"
          style={{ ...CD_INPUT, minHeight: "140px", resize: "none", lineHeight: 1.6 }}
        />
      </div>

      {/* Experience */}
      <div style={{ marginTop: "14px", maxWidth: "220px" }}>
        <Label style={CD_LABEL}>Years of experience (optional)</Label>
        <Input
          type="number"
          value={formData.experience_years}
          onChange={(e) => handleChange("experience_years", e.target.value)}
          placeholder="8"
          min="0"
          max="50"
          style={CD_INPUT}
          className="placeholder:text-white/40 focus-visible:ring-0 focus-visible:ring-offset-0"
        />
      </div>
    </div>
  );

  // Step 5: Showcase
  const renderShowcaseStep = () => (
    <div>
      <h2 className="cd-display" style={{ fontWeight: 800, fontSize: "22px", color: "#fff", letterSpacing: "-.5px", margin: 0 }}>Showcase your work</h2>
      <p style={{ fontSize: "12.5px", color: "rgba(255,255,255,.55)", marginTop: "6px" }}>Upload images that showcase your best work.</p>

      {/* Portfolio Images */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "10px", marginTop: "14px" }}>
        {formData.portfolio_images.map((img, index) => (
          <div key={index} className="relative group" style={{ aspectRatio: "1", borderRadius: "12px", overflow: "hidden" }}>
            <img
              src={img.url || img}
              alt={`Portfolio ${index + 1}`}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            <button
              type="button"
              onClick={() => removePortfolioImage(index)}
              className="opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ position: "absolute", top: "6px", right: "6px", width: "24px", height: "24px", background: "rgba(220,38,38,.9)", borderRadius: "999px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
            >
              <X className="w-3.5 h-3.5 text-white" />
            </button>
          </div>
        ))}

        {/* Upload button */}
        <button
          type="button"
          onClick={() => portfolioInputRef.current?.click()}
          disabled={uploadingPortfolio}
          style={{ aspectRatio: "1", borderRadius: "12px", border: "1.5px dashed rgba(255,255,255,.18)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "4px", color: "#28C0D0", cursor: "pointer", background: "transparent" }}
        >
          {uploadingPortfolio ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            <Plus className="w-6 h-6" />
          )}
        </button>
      </div>
      <input
        ref={portfolioInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handlePortfolioUpload}
        className="hidden"
      />

      {/* Video Links */}
      <div style={{ marginTop: "18px" }}>
        <Label style={CD_LABEL}>Video links</Label>
        {formData.video_links.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "10px" }}>
            {formData.video_links.map((link, index) => (
              <div key={index} style={{ display: "flex", alignItems: "center", gap: "10px", background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.1)", borderRadius: "11px", padding: "10px 14px" }}>
                <span style={{ flex: 1, color: "#fff", fontSize: "12.5px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{link}</span>
                <button
                  type="button"
                  onClick={() => removeVideoLink(index)}
                  style={{ width: "24px", height: "24px", background: "rgba(248,113,113,.15)", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                >
                  <X className="w-3.5 h-3.5" style={{ color: "#F87171" }} />
                </button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: "flex", gap: "10px" }}>
          <Input
            value={newVideoLink}
            onChange={(e) => setNewVideoLink(e.target.value)}
            placeholder="https://youtube.com/…"
            style={CD_INPUT}
            className="placeholder:text-white/40 focus-visible:ring-0 focus-visible:ring-offset-0"
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addVideoLink())}
          />
          <Button
            type="button"
            onClick={addVideoLink}
            style={{ padding: "0 18px", borderRadius: "11px", background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.16)", color: "#fff", fontWeight: 600, fontSize: "13px", height: "auto" }}
          >
            Add
          </Button>
        </div>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderWelcomeStep();
      case 2: return renderDetailsStep();
      case 3: return renderRolesStep();
      case 4: return renderAboutStep();
      case 5: return renderShowcaseStep();
      default: return null;
    }
  };

  const isLast = currentStep === 5;

  return (
    <div
      className="cd-root"
      style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 20px" }}
    >
      {/* Ambient blooms + rotating brand watermark */}
      <div className="cd-bloom-wrap">
        <div className="cd-bloom cd-bloom-1" />
        <div className="cd-bloom cd-bloom-2" />
      </div>
      <img
        src="/assets/shootzu-icon.png"
        alt=""
        aria-hidden="true"
        style={{ position: "absolute", right: "-90px", bottom: "-90px", width: "420px", height: "420px", opacity: 0.05, pointerEvents: "none", zIndex: 0, objectFit: "contain" }}
      />

      {/* Content */}
      <div style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: "660px" }}>
        {/* Glass Card */}
        <div style={{ background: "rgba(18,22,38,.72)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)", border: "1px solid rgba(255,255,255,.12)", borderRadius: "24px", boxShadow: "0 40px 90px -30px rgba(0,0,0,.7)", overflow: "hidden" }}>
          {/* Header */}
          <div style={{ padding: "26px 30px 0" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <img src="/assets/shootzu-logo-white.png" alt="Shootzu" style={{ height: "34px", width: "auto" }} />
              <span className="cd-display" style={{ fontWeight: 700, fontSize: "12px", color: "rgba(255,255,255,.55)" }}>
                Step {currentStep} of {steps.length}
              </span>
            </div>

            {/* Stepper */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "22px" }}>
              {steps.map((step) => {
                const StepIcon = step.icon;
                const done = currentStep > step.number;
                const active = currentStep === step.number;
                const dotStyle = done
                  ? { background: "rgba(103,198,180,.18)", color: "#67C6B4", border: "none", boxShadow: "none" }
                  : active
                  ? { background: "linear-gradient(135deg,#572B7C,#78287C)", color: "#fff", border: "none", boxShadow: "0 8px 20px -8px rgba(120,40,124,.8)" }
                  : { background: "rgba(255,255,255,.05)", color: "rgba(255,255,255,.35)", border: "1px solid rgba(255,255,255,.1)", boxShadow: "none" };
                const lblColor = done ? "rgba(255,255,255,.6)" : active ? "#fff" : "rgba(255,255,255,.35)";
                return (
                  <div key={step.number} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "7px" }}>
                    <span style={{ width: "38px", height: "38px", borderRadius: "11px", display: "flex", alignItems: "center", justifyContent: "center", transition: "all .2s ease", ...dotStyle }}>
                      {done ? <CheckCircle2 className="w-[18px] h-[18px]" /> : <StepIcon className="w-[18px] h-[18px]" />}
                    </span>
                    <span className="cd-display" style={{ fontSize: "10.5px", fontWeight: 600, color: lblColor, textAlign: "center" }}>{step.title}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ height: "1px", background: "rgba(255,255,255,.08)", margin: "22px 0 0" }} />

          {/* Step Content */}
          <div style={{ padding: "24px 30px", minHeight: "300px" }}>
            {renderCurrentStep()}
          </div>

          {/* Navigation Buttons */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", padding: "18px 30px", borderTop: "1px solid rgba(255,255,255,.08)" }}>
            <Button
              type="button"
              onClick={goToPrevStep}
              disabled={currentStep === 1}
              style={{ padding: "12px 22px", borderRadius: "12px", background: "transparent", border: "1px solid rgba(255,255,255,.16)", color: "rgba(255,255,255,.75)", fontWeight: 600, fontSize: "13.5px", height: "auto", opacity: currentStep === 1 ? 0.4 : 1, pointerEvents: currentStep === 1 ? "none" : "auto" }}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>

            {!isLast ? (
              <Button
                type="button"
                onClick={goToNextStep}
                disabled={loading}
                style={{ display: "flex", alignItems: "center", gap: "8px", padding: "12px 26px", borderRadius: "12px", background: "#28C0D0", border: "none", color: "#0C1120", fontWeight: 700, fontSize: "13.5px", height: "auto", boxShadow: "0 12px 28px -12px rgba(40,192,208,.7)" }}
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {currentStep === 1 ? "Let's get started" : "Continue"}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSubmitForReview}
                disabled={submitting}
                style={{ display: "flex", alignItems: "center", gap: "8px", padding: "12px 26px", borderRadius: "12px", background: "#15803D", border: "none", color: "#fff", fontWeight: 700, fontSize: "13.5px", height: "auto", boxShadow: "0 12px 28px -12px rgba(21,128,61,.7)" }}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Submitting…
                  </>
                ) : (
                  <>
                    Submit for review
                    <CheckCircle2 className="w-4 h-4" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Bottom Text */}
        <p style={{ textAlign: "center", color: "rgba(255,255,255,.5)", fontSize: "12.5px", marginTop: "20px" }}>
          © {new Date().getFullYear()} Shootzu is Powered by Turbo 360
        </p>
        <div style={{ textAlign: "center", marginTop: "8px" }}>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                type="button"
                className="hover:text-red-400 transition-colors"
                style={{ color: "rgba(255,255,255,.4)", fontSize: "12px", background: "transparent", border: "none", cursor: "pointer" }}
              >
                Delete Account
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[#1e1534] border-gray-700">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-white">
                  Are you sure you want to delete your account?
                </AlertDialogTitle>
                <AlertDialogDescription className="text-gray-300">
                  This action cannot be undone. Your account and all associated data will be permanently deleted.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="border-gray-700 hover:bg-gray-800 text-white">
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteAccount}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Yes, Delete My Account
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
};

export default CrewOnboarding;
